import React, { useCallback, useMemo, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { useTranslation } from 'react-i18next'
import { CgMaximize } from 'react-icons/cg'
import Video from 'components/Video'
import { SLIDESHOW_RATIO } from 'components/Video/videoRatio'
import { getCookieConsent, useCookieConsents } from 'components/GDPRPopup/useCookiePreferences'
import Panorama360 from 'components/Panorama360'
import Picture from 'components/Picture'
import SketchFab from 'components/SketchFab'
import useEmblaCarousel from 'embla-carousel-react'
import { useTheme } from 'styled-components'
import { getButtonColor, getButtonTextColor } from 'styles/functions'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'
import { NextButton, PrevButton, usePrevNextButtons } from './EmblaCarouselArrowButtons'
import { NocookieConsentPlaceholder } from 'components/NocookieConsentPlaceholder'
import SlideCaptionRow from './SlideCaptionRow'
import { IMAGE_TYPES, VIDEO_TYPES, isSlideRenderable } from './slideTypes'
import SlideButton from './SlideButton'

const SlideShowLightbox = dynamic(() => import('./SlideShowLightbox'), { ssr: false })

const isLightboxable = (slide) =>
  IMAGE_TYPES.includes(slide.type) ||
  VIDEO_TYPES.includes(slide.type) ||
  slide.type === 'sketchfab' ||
  slide.type === '360'

const DRAG_CLICK_THRESHOLD_PX = 10

const slideTitleStyle = {
  margin: 0,
  fontSize: '24px',
  fontWeight: '600',
  color: '#333',
  textAlign: 'center',
}

const legendFontSize = (legendSize) => (legendSize ? (legendSize === 'small' ? '13px' : '16px') : '13px')

const SlideShow = ({ part, themeInfos }) => {
  const online = useOnlineStatus()
  const theme = useTheme()
  const { t } = useTranslation()
  const cookiePreferences = useCookieConsents();

  const { slides } = part;

  const renderableSlides = useMemo(
    () => (slides || []).filter((slide) => isSlideRenderable(slide, online)),
    [slides, online]
  );

  const [emblaRef, emblaApi] = useEmblaCarousel({
    watchDrag: (_, event) => {
      const target = event.target;
      return target instanceof HTMLImageElement ||
        target instanceof HTMLIFrameElement ||
        event.target.className === 'embla__slide__interactive__left--360' ||
        event.target.className === 'embla__slide__interactive__right' ||
        event.target.className === 'embla__slide__interactive__left' ||
        event.target.className === 'embla__slide__interactive__right_sketchfab';

    }
  });

  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } =
    usePrevNextButtons(emblaApi)

  const { lightboxSlides, toLightboxIndex, toCarouselIndex } = useMemo(() => {
    const lightboxSlides = [];
    const toLightboxIndex = new Map();
    const toCarouselIndex = [];

    renderableSlides.forEach((slide, i) => {
      if (!isLightboxable(slide)) return;
      toLightboxIndex.set(i, lightboxSlides.length);
      toCarouselIndex.push(i);

      const common = {
        title: slide.title,
        legend: slide.legend,
        legendSize: slide.legendSize,
      };

      lightboxSlides.push(
        IMAGE_TYPES.includes(slide.type)
          ? { ...common, type: 'image', src: slide.url, alt: slide.alt || `slide image ${i}` }
          : { ...common, type: slide.type, part: slide }
      );
    });

    return { lightboxSlides, toLightboxIndex, toCarouselIndex };
  }, [renderableSlides]);

  const pictureCaptions = useMemo(() => {
    const titles = [];
    const legends = [];
    const captionIndexes = new Map();

    renderableSlides.forEach((slide, i) => {
      if (!IMAGE_TYPES.includes(slide.type)) return;
      captionIndexes.set(i, titles.length);
      titles.push(slide.title || '');
      legends.push({ text: slide.legend || '', size: slide.legendSize });
    });

    return {
      titles,
      legends,
      captionIndexes,
      hasTitle: titles.some((title) => title.length > 0),
      hasLegend: legends.some(({ text }) => text.length > 0),
    };
  }, [renderableSlides]);

  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const viewedIndex = useRef(0);
  const pointerStart = useRef(null);

  const openLightbox = useCallback((carouselIndex) => {
    const idx = toLightboxIndex.get(carouselIndex);
    if (idx === undefined) return;
    viewedIndex.current = idx;
    setLightboxIndex(idx);
    setLightboxOpen(true);
  }, [toLightboxIndex]);

  const closeLightbox = useCallback(() => {
    const target = toCarouselIndex[viewedIndex.current];
    if (target !== undefined) emblaApi?.scrollTo(target, true);
    setLightboxOpen(false);
  }, [emblaApi, toCarouselIndex]);

  const handleImagePointerDown = useCallback((event) => {
    pointerStart.current = { x: event.clientX, y: event.clientY };
  }, []);

  const handleImageClick = useCallback((carouselIndex) => (event) => {
    const start = pointerStart.current;
    pointerStart.current = null;
    if (start) {
      const travel = Math.hypot(event.clientX - start.x, event.clientY - start.y);
      if (travel > DRAG_CLICK_THRESHOLD_PX) return;
    }
    openLightbox(carouselIndex);
  }, [openLightbox]);


  const renderSlideButton = (slide) =>
    slide.withButton ? <SlideButton slide={slide} themeInfos={themeInfos} /> : null;

  const renderExpandButton = (index) => (
    <button
      type="button"
      className="embla__slide__expand"
      onClick={() => openLightbox(index)}
      aria-label={t('slideShow.fullscreen')}
      title={t('slideShow.fullscreen')}
    >
      <CgMaximize />
    </button>
  );

  return (
    <section
      className="embla"
      style={{
        '--embla-button-color': getButtonColor(theme, themeInfos),
        '--embla-button-text-color': getButtonTextColor(theme),
      }}
    >
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {renderableSlides.map((slide, index) => {
            if (!getCookieConsent(cookiePreferences, slide.type)) {
              return <div key={index} className="embla__slide">
                <NocookieConsentPlaceholder mediaType={slide.type} />
              </div>
            }

            if (slide.type === 'Picture' || slide.type === 'Url') {
              const captionIndex = pictureCaptions.captionIndexes.get(index)

              return (
                <div key={index} className="embla__slide embla__slide--picture embla__slide--expandable">
                  {pictureCaptions.hasTitle && (
                    <SlideCaptionRow
                      className="embla__slide__title"
                      items={pictureCaptions.titles}
                      activeIndex={captionIndex}
                      renderItem={(title) => <h2 style={slideTitleStyle}>{title}</h2>}
                    />
                  )}
                  <div className="embla__slide__media">
                    <Picture className={"embla__slide--image"} url={slide.url} alt={`slide image ${index}`} withZoom={false} onPointerDown={handleImagePointerDown} onClick={handleImageClick(index)}  />
                    {renderSlideButton(slide)}
                  </div>
                  {pictureCaptions.hasLegend && (
                    <SlideCaptionRow
                      className="embla__slide__legend"
                      items={pictureCaptions.legends}
                      activeIndex={captionIndex}
                      renderItem={({ text, size }) => (
                        <legend style={{ fontSize: legendFontSize(size) }}>{text}</legend>
                      )}
                    />
                  )}
                  {renderExpandButton(index)}
                </div>
              )
            }
            else if (slide.type === 'video' || slide.type === 'youtube' || slide.type === 'vimeo') {
              return (
                <div key={index} className={'embla__slide embla__slide__interactive embla__slide--expandable'}>
                  <div className="embla__slide__interactive__left" />
                  <Video
                    url={slide.url}
                    type={slide.type}
                    title={slide.title}
                    legend={slide.legend}
                    legendSize={slide.legendSize}
                    style={slide.style}
                    forcePause={lightboxOpen}
                    pinnedRatio={SLIDESHOW_RATIO}
                  />
                  <div className="embla__slide__interactive__right" />
                  {renderSlideButton(slide)}
                  {renderExpandButton(index)}
                </div>
              )
            } else if (slide.type === 'sketchfab') {
              return (
                <div key={index} className={'embla__slide embla__slide__interactive embla__slide--expandable embla__slide--sketchfab'}>
                  <div className="embla__slide__interactive__left" />
                    <SketchFab part={slide} className="embla__slide__sketchfab" />
                  <div className="embla__slide__interactive__right_sketchfab" />
                  {renderSlideButton(slide)}
                  {renderExpandButton(index)}
                </div>
              )
            }
            else if (slide.type === '360') {
              return (
                <div key={index} className={'embla__slide embla__slide--expandable'}>
                  <div className="embla__slide__interactive__left--360" />
                    <Panorama360 part={slide} variant="carousel" />
                  <div className="embla__slide__interactive__right" />
                  {renderSlideButton(slide)}
                  {renderExpandButton(index)}
                </div>
              )
            }
            return null
          })}
        </div>
      </div>

      <div className="embla__controls">
        <div className="embla__buttons">
          <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
          <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
        </div>
      </div>

      {lightboxIndex >= 0 && (
        <SlideShowLightbox
          slides={lightboxSlides}
          index={Math.min(lightboxIndex, lightboxSlides.length - 1)}
          open={lightboxOpen}
          onClose={closeLightbox}
          onView={(i) => { viewedIndex.current = i; }}
          themeInfos={themeInfos}
        />
      )}
    </section>
  )
}

export default SlideShow